/**
 * @page /idle-time
 * @description Leerlaufzeit-Analyse — Idle-Time zwischen Auftraegen pro Mitarbeiter
 *   mit Ranking, Trend-Charts und Tages-Timeline.
 * @features
 *   - KPI-Cards (Durchschnitt, Median, Gesamtkosten)
 *   - MA-Ranking mit sortierbarer Tabelle und CSV-Export
 *   - Trend-Chart (Tag/Woche/Monat Granularitaet)
 *   - Tages-Timeline mit Einzelpausen-Visualisierung
 *   - Filter nach Zeitraum, Typ (Pack/Pick) und Mitarbeiter
 * @api GET /api/reports/idle-time/employees
 * @api GET /api/reports/idle-time/summary
 * @api GET /api/reports/idle-time/trend
 * @api GET /api/reports/idle-time/timeline
 * @roles admin, manager
 * @i18n idleTime
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import useTabParam from '../hooks/useTabParam';
import { getDateRange } from '../utils/dateUtils';
import DateRangeFilter from '../components/DateRangeFilter';
import { PageHeader } from '../components/ui/PageHeader';
import { KpiCard } from '../components/ui/KpiCard';
import { Badge } from '../components/ui/Badge';
import { useLanguage } from '../context/LanguageContext';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart, Legend,
} from 'recharts';
import { Download, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const VIOLET = '#338edc';
const CYAN = '#22D3EE';
const GRAY = '#E5E7EB';
const AMBER = '#F59E0B';

function fmtMin(m) {
  if (m == null || isNaN(m)) return '–';
  return Number(m).toFixed(1).replace('.', ',') + ' min';
}

function fmtH(h) {
  if (h == null || isNaN(h)) return '–';
  return Number(h).toFixed(1).replace('.', ',') + ' h';
}

function fmtPct(p) {
  if (p == null || isNaN(p)) return '–';
  return Number(p).toFixed(1).replace('.', ',') + '%';
}

function fmtEur(e) {
  if (e == null || isNaN(e)) return '–';
  return Number(e).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

export default function IdleTime() {
  const { t } = useLanguage();

  // Date range
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [preset, setPreset] = useState('');

  // Filters
  const [type, setType] = useState('pack');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);

  // Tab
  const [activeTab, setActiveTab] = useTabParam('ranking');

  // Data
  const [summaryData, setSummaryData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Timeline controls
  const [timelineEmployee, setTimelineEmployee] = useState('');
  const [timelineDate, setTimelineDate] = useState('');

  // Trend controls
  const [granularity, setGranularity] = useState('day');

  // Leaderboard sort
  const [sortCol, setSortCol] = useState('avg_idle_minutes');
  const [sortDir, setSortDir] = useState('asc');

  // Initialize date range
  useEffect(() => {
    if (!von) {
      const range = getDateRange('diese_woche');
      setVon(range.von);
      setBis(range.bis);
      setPreset('diese_woche');
    }
  }, []);

  // Fetch employees for dropdown
  useEffect(() => {
    if (!von || !bis) return;
    fetch(`${API}/api/reports/idle-time/employees?von=${von}&bis=${bis}&type=${type}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { employees: [] })
      .then(d => setEmployees(d.employees || []))
      .catch(() => setEmployees([]));
  }, [von, bis, type]);

  // Fetch summary data
  useEffect(() => {
    if (!von || !bis) return;
    setLoading(true);
    setError(null);

    const empParam = selectedEmployee ? `&employee=${encodeURIComponent(selectedEmployee)}` : '';
    const url = `${API}/api/reports/idle-time/summary?von=${von}&bis=${bis}&type=${type}${empParam}`;

    fetch(url, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error('Fehler beim Laden'); return r.json(); })
      .then(d => { setSummaryData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [von, bis, type, selectedEmployee]);

  // Fetch trend data when trend tab active
  useEffect(() => {
    if (activeTab !== 'trend' || !von || !bis) return;
    const empParam = selectedEmployee ? `&employee=${encodeURIComponent(selectedEmployee)}` : '';
    fetch(`${API}/api/reports/idle-time/trend?von=${von}&bis=${bis}&type=${type}&granularity=${granularity}${empParam}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { trend: [] })
      .then(d => setTrendData(d))
      .catch(() => setTrendData({ trend: [] }));
  }, [activeTab, von, bis, type, granularity, selectedEmployee]);

  // Fetch timeline data when timeline tab active
  useEffect(() => {
    if (activeTab !== 'timeline' || !timelineEmployee || !timelineDate) return;
    fetch(`${API}/api/reports/idle-time/timeline?date=${timelineDate}&employee=${encodeURIComponent(timelineEmployee)}&type=${type}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setTimelineData(d))
      .catch(() => setTimelineData(null));
  }, [activeTab, timelineEmployee, timelineDate, type]);

  // Export handler
  const handleExport = useCallback(() => {
    window.open(`${API}/api/reports/idle-time/export?von=${von}&bis=${bis}&type=${type}`, '_blank');
  }, [von, bis, type]);

  // Sorted leaderboard
  const sortedLeaderboard = useMemo(() => {
    if (!summaryData?.leaderboard) return [];
    const list = [...summaryData.leaderboard];
    list.sort((a, b) => {
      const av = a[sortCol] ?? 0;
      const bv = b[sortCol] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [summaryData, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const summary = summaryData?.summary;

  return (
    <div className="p-6">
      {/* Header */}
      <PageHeader
        title={t('idleTime.title')}
        subtitle={t('idleTime.subtitle')}
      />

      {/* Filter Bar */}
      <DateRangeFilter
        von={von} bis={bis} preset={preset}
        onChange={({ von: v, bis: b, preset: p }) => { setVon(v); setBis(b); setPreset(p); }}
        className="mb-4"
      >
        {/* Type toggle */}
        <div className="flex items-center gap-1 ml-4 bg-surface-inset rounded-lg p-0.5 border border-line">
          {['pick', 'pack'].map(t2 => (
            <button key={t2} onClick={() => setType(t2)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${type === t2
                ? 'bg-surface-card text-content shadow-sm border border-line'
                : 'text-content-secondary hover:text-content'}`}>
              {t(`idleTime.${t2}`)}
            </button>
          ))}
        </div>

        {/* Employee dropdown */}
        <select
          value={selectedEmployee}
          onChange={e => setSelectedEmployee(e.target.value)}
          className="ml-3 px-3 py-1.5 text-xs bg-surface-inset border border-line rounded-lg text-content"
        >
          <option value="">{t('idleTime.selectEmployee')}</option>
          {employees.map(e => (
            <option key={e.name} value={e.name}>
              {e.name}{e.is_shared ? ' (Shared)' : ''}
            </option>
          ))}
        </select>

        {/* Export */}
        <button onClick={handleExport}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content-secondary hover:text-content border border-line rounded-lg hover:bg-surface-hover transition-colors">
          <Download className="w-3.5 h-3.5" />
          {t('idleTime.export')}
        </button>
      </DateRangeFilter>

      {/* Loading / Error */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      )}
      {error && (
        <div className="text-center py-10 text-red-500 text-sm">{error}</div>
      )}

      {!loading && !error && summary && (
        <>
          {/* 6 KPI Cards - 3x2 grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <KpiCard label={t('idleTime.avgIdle')} value={fmtMin(summary.avg_idle_minutes)} />
            <KpiCard label={t('idleTime.median')} value={fmtMin(summary.median_idle_minutes)} />
            <KpiCard label={t('idleTime.totalIdleH')} value={fmtH(summary.total_idle_hours)} />
            <KpiCard label={t('idleTime.gapsAnalyzed')} value={summary.total_gaps_analyzed?.toLocaleString('de-DE') || '0'}
              tooltip={`${summary.employees_analyzed} ${t('idleTime.employeesAnalyzed')}\n${summary.outliers_excluded} ${t('idleTime.outliersExcluded')}`}
            />
            <KpiCard label={t('idleTime.productivity')} value={fmtPct(summary.productivity_percent)} />
            <KpiCard label={t('idleTime.idleCost')} value={fmtEur(summary.idle_cost_eur)}
              tooltip={t('idleTime.costNote')}
            />
          </div>

          {/* Time Comparison Chart */}
          {summaryData?.time_comparison?.length > 0 && (
            <TimeComparisonChart data={summaryData.time_comparison} t={t} />
          )}

          {/* Tabs */}
          <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-1 border border-[var(--border-default,#E5E7EB)] mb-6">
            {[
              { key: 'ranking', label: t('idleTime.ranking'), badge: sortedLeaderboard.length },
              { key: 'trend', label: t('idleTime.trend') },
              { key: 'timeline', label: t('idleTime.timeline') },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}>
                {tab.label}
                {tab.badge > 0 && <Badge variant="default">{tab.badge}</Badge>}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'ranking' && <RankingTab leaderboard={sortedLeaderboard} toggleSort={toggleSort} SortIcon={SortIcon} t={t} />}
          {activeTab === 'trend' && (
            <TrendTab
              trendData={trendData} granularity={granularity} setGranularity={setGranularity}
              selectedEmployee={selectedEmployee} t={t}
            />
          )}
          {activeTab === 'timeline' && (
            <TimelineTab
              employees={employees} timelineEmployee={timelineEmployee}
              setTimelineEmployee={setTimelineEmployee}
              timelineDate={timelineDate} setTimelineDate={setTimelineDate}
              timelineData={timelineData} t={t}
            />
          )}
        </>
      )}

      {!loading && !error && !summary && (
        <div className="text-center py-20 text-[var(--text-secondary)] text-sm">{t('idleTime.noData')}</div>
      )}
    </div>
  );
}

/* ═══════════ Time Comparison Chart ═══════════ */
function TimeComparisonChart({ data, t }) {
  const chartData = data.map(d => ({
    name: d.employee_name?.length > 14 ? d.employee_name.slice(0, 12) + '…' : d.employee_name,
    fullName: d.employee_name,
    [t('idleTime.activeTime')]: d.active_hours,
    [t('idleTime.idleTime')]: d.idle_hours,
    [t('idleTime.pauseTime')]: d.pause_hours,
    [t('idleTime.unaccountedTime')]: d.unaccounted_hours,
  }));

  const activeKey = t('idleTime.activeTime');
  const idleKey = t('idleTime.idleTime');
  const pauseKey = t('idleTime.pauseTime');
  const unaccKey = t('idleTime.unaccountedTime');

  return (
    <div className="bg-surface-card border border-line-card rounded-card p-6 mb-6">
      <h3 className="text-sm font-semibold text-content mb-4">{t('idleTime.timeComparison')}</h3>
      <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 40 + 40)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-line)" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} unit=" h" />
          <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
          <Tooltip
            contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-line)', borderRadius: 12, fontSize: 12 }}
            formatter={(value, name) => [Number(value).toFixed(1).replace('.', ',') + ' h', name]}
            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey={activeKey} stackId="a" fill={VIOLET} radius={0} />
          <Bar dataKey={idleKey} stackId="a" fill={CYAN} radius={0} />
          <Bar dataKey={pauseKey} stackId="a" fill={GRAY} radius={0} />
          <Bar dataKey={unaccKey} stackId="a" fill={AMBER} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ═══════════ Ranking Tab ═══════════ */
function RankingTab({ leaderboard, toggleSort, SortIcon, t }) {
  if (!leaderboard.length) return <div className="text-center py-10 text-[var(--text-secondary)] text-sm">{t('idleTime.noData')}</div>;

  const cols = [
    { key: 'rank', label: t('idleTime.rank'), align: 'text-center', w: 'w-14', sortable: false },
    { key: 'employee_name', label: t('idleTime.employee'), align: 'text-left', sortable: false },
    { key: 'avg_idle_minutes', label: t('idleTime.avgGap'), align: 'text-right' },
    { key: 'median_idle_minutes', label: t('idleTime.medianGap'), align: 'text-right' },
    { key: 'total_gaps', label: t('idleTime.totalGaps'), align: 'text-right' },
    { key: 'total_idle_hours', label: t('idleTime.totalIdleH'), align: 'text-right' },
    { key: 'total_active_hours', label: t('idleTime.activeTimeH'), align: 'text-right' },
    { key: 'productivity_percent', label: t('idleTime.productivityPct'), align: 'text-right' },
    { key: 'idle_cost_eur', label: t('idleTime.idleCostEur'), align: 'text-right' },
    { key: 'score', label: t('idleTime.score'), align: 'text-right' },
  ];

  const rankBadge = (rank) => {
    if (rank === 1) return <span className="text-amber-400 font-bold">1</span>;
    if (rank === 2) return <span className="text-[var(--text-tertiary,#6B7280)] font-bold">2</span>;
    if (rank === 3) return <span className="text-orange-400 font-bold">3</span>;
    return <span className="text-[var(--text-secondary)]">{rank}</span>;
  };

  return (
    <div className="bg-surface-card border border-line-card rounded-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line">
              {cols.map(c => (
                <th key={c.key}
                  onClick={() => c.sortable !== false && toggleSort(c.key)}
                  className={`px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] ${c.align} ${c.w || ''} ${c.sortable !== false ? 'cursor-pointer hover:text-content select-none' : ''}`}>
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable !== false && <SortIcon col={c.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((row) => (
              <tr key={row.employee_name} className="border-b border-line-subtle hover:bg-surface-hover transition-colors">
                <td className="px-3 py-3 text-center">{rankBadge(row.rank)}</td>
                <td className="px-3 py-3 text-left font-medium text-content">
                  {row.employee_name}
                  {row.is_shared_account && (
                    <Badge variant="outline" className="ml-2 text-[9px]">{t('idleTime.sharedAccount')}</Badge>
                  )}
                </td>
                <td className="px-3 py-3 text-right font-mono text-content">{fmtMin(row.avg_idle_minutes)}</td>
                <td className="px-3 py-3 text-right font-mono text-content">{fmtMin(row.median_idle_minutes)}</td>
                <td className="px-3 py-3 text-right text-content">{row.total_gaps}</td>
                <td className="px-3 py-3 text-right font-mono text-content">{fmtH(row.total_idle_hours)}</td>
                <td className="px-3 py-3 text-right font-mono text-content">{fmtH(row.total_active_hours)}</td>
                <td className="px-3 py-3 text-right font-mono text-content">{fmtPct(row.productivity_percent)}</td>
                <td className="px-3 py-3 text-right font-mono text-content">{fmtEur(row.idle_cost_eur)}</td>
                <td className="px-3 py-3 text-right">
                  <ScoreBadge score={row.score} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScoreBadge({ score }) {
  let dotColor = 'bg-emerald-500';
  if (score < 50) dotColor = 'bg-red-500';
  else if (score < 75) dotColor = 'bg-amber-500';
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--text-primary)]">
      <span className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
      {score}
    </span>
  );
}

/* ═══════════ Trend Tab ═══════════ */
function TrendTab({ trendData, granularity, setGranularity, selectedEmployee, t }) {
  const trend = trendData?.trend || [];

  const chartData = useMemo(() => {
    return trend.map(d => ({
      date: d.date,
      label: d.date.slice(5), // MM-DD
      avg: d.avg_idle_minutes,
      median: d.median_idle_minutes,
      productivity: d.productivity_percent,
    }));
  }, [trend]);

  return (
    <div>
      {/* Granularity toggle */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-[var(--text-secondary)]">{t('idleTime.granularity')}:</span>
        <div className="flex gap-1 bg-surface-inset rounded-lg p-0.5 border border-line">
          {['day', 'week'].map(g => (
            <button key={g} onClick={() => setGranularity(g)}
              className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                granularity === g
                  ? 'bg-surface-card text-content shadow-sm border border-line'
                  : 'text-content-secondary hover:text-content'
              }`}>
              {t(`idleTime.${g}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-surface-card border border-line-card rounded-card p-6">
        {chartData.length === 0 ? (
          <div className="text-center py-10 text-[var(--text-secondary)] text-sm">{t('idleTime.noData')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-line)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} unit=" min" />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: CYAN }} unit="%" domain={[0, 100]} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-line)', borderRadius: 12, fontSize: 12 }}
                formatter={(value, name) => {
                  if (name === t('idleTime.productivity')) return [fmtPct(value), name];
                  return [fmtMin(value), name];
                }}
                labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left" type="monotone" dataKey="avg" stroke={VIOLET} strokeWidth={2} dot={{ r: 3, fill: VIOLET }} name={t('idleTime.avgIdle')} />
              <Line yAxisId="left" type="monotone" dataKey="median" stroke={GRAY} strokeWidth={1.5} strokeDasharray="4 4" dot={false} name={t('idleTime.median')} />
              <Line yAxisId="right" type="monotone" dataKey="productivity" stroke={CYAN} strokeWidth={2} dot={{ r: 3, fill: CYAN }} name={t('idleTime.productivity')} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

/* ═══════════ Timeline Tab ═══════════ */
function TimelineTab({ employees, timelineEmployee, setTimelineEmployee, timelineDate, setTimelineDate, timelineData, t }) {
  return (
    <div>
      {/* Controls */}
      <div className="flex items-center gap-3 mb-4">
        <select value={timelineEmployee} onChange={e => setTimelineEmployee(e.target.value)}
          className="px-3 py-2 text-sm bg-surface-inset border border-line rounded-lg text-content min-w-[200px]">
          <option value="">{t('idleTime.selectEmployee')}</option>
          {employees.map(e => (
            <option key={e.name} value={e.name}>{e.name}</option>
          ))}
        </select>
        <input type="date" value={timelineDate} onChange={e => setTimelineDate(e.target.value)}
          className="px-3 py-2 text-sm bg-surface-inset border border-line rounded-lg text-content" />
      </div>

      {!timelineEmployee || !timelineDate ? (
        <div className="text-center py-10 text-[var(--text-secondary)] text-sm">
          {t('idleTime.selectEmployee')} & {t('idleTime.selectDate')}
        </div>
      ) : !timelineData ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Day Summary Cards (hours-based) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <KpiCard label={t('idleTime.activities')} value={timelineData.activities?.length || 0} />
            <KpiCard label={t('idleTime.activeTime')} value={fmtH(timelineData.day_summary?.total_active_hours)} />
            <KpiCard label={t('idleTime.idleTime')} value={fmtH(timelineData.day_summary?.total_idle_hours)} />
            <KpiCard label={t('idleTime.pauseTime')} value={fmtH(timelineData.day_summary?.total_pause_hours)} />
            <KpiCard label={t('idleTime.unaccountedTime')} value={fmtH(timelineData.day_summary?.unaccounted_hours)} />
            <KpiCard label={t('idleTime.avgGap')} value={fmtMin(timelineData.day_summary?.avg_gap_minutes)} />
            <KpiCard label={t('idleTime.productivity')} value={fmtPct(timelineData.day_summary?.productivity_percent)} />
          </div>

          {/* Visual Timeline */}
          <TimelineVisual data={timelineData} t={t} />

          {/* Gaps Table */}
          <div className="mt-6 bg-surface-card border border-line-card rounded-card overflow-hidden">
            <div className="px-4 py-3 border-b border-line">
              <h3 className="text-sm font-semibold text-content">{t('idleTime.gaps')} ({timelineData.gaps?.length || 0})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line">
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] text-left">{t('idleTime.gapAfter')}</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] text-left">{t('idleTime.gapStart')}</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] text-left">{t('idleTime.gapEnd')}</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] text-right">{t('idleTime.rawGap')}</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] text-right">{t('idleTime.pause')}</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] text-right">{t('idleTime.idleNet')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(timelineData.gaps || []).map((g, i) => (
                    <tr key={i} className={`border-b border-line-subtle hover:bg-surface-hover transition-colors ${g.is_outlier ? 'bg-amber-500/5' : ''}`}>
                      <td className="px-4 py-2.5 text-content">#{g.after_activity + 1}</td>
                      <td className="px-4 py-2.5 text-content font-mono">{g.start}</td>
                      <td className="px-4 py-2.5 text-content font-mono">{g.end}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-content">{fmtMin(g.raw_minutes)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-[var(--text-secondary)]">{fmtMin(g.pause_minutes)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-content font-semibold">
                        {fmtMin(g.idle_minutes)}
                        {g.is_outlier && <Badge variant="outline" className="ml-2 text-[9px] text-amber-500">{t('idleTime.outlier')}</Badge>}
                      </td>
                    </tr>
                  ))}
                  {(!timelineData.gaps || timelineData.gaps.length === 0) && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-[var(--text-secondary)] text-sm">{t('idleTime.noData')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════ Visual Timeline Bar ═══════════ */
function TimelineVisual({ data, t }) {
  if (!data?.activities?.length) return null;

  const activities = data.activities;
  const parseTime = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  const allTimes = [
    ...activities.map(a => parseTime(a.start)),
    ...activities.map(a => parseTime(a.end)),
    ...(data.attendance || []).filter(a => a.check_in).map(a => parseTime(a.check_in)),
    ...(data.attendance || []).filter(a => a.check_out).map(a => parseTime(a.check_out)),
  ];

  const minTime = Math.max(0, Math.min(...allTimes) - 30);
  const maxTime = Math.min(24 * 60, Math.max(...allTimes) + 30);
  const totalRange = maxTime - minTime;
  if (totalRange <= 0) return null;

  const pct = (min) => ((min - minTime) / totalRange * 100);
  const widthPct = (start, end) => ((end - start) / totalRange * 100);

  // Hour markers
  const startHour = Math.ceil(minTime / 60);
  const endHour = Math.floor(maxTime / 60);
  const hours = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);

  return (
    <div className="bg-surface-card border border-line-card rounded-card p-4">
      <h3 className="text-sm font-semibold text-content mb-3">{t('idleTime.timeline')}</h3>
      <div className="relative h-12 bg-surface-inset rounded-lg overflow-hidden">
        {/* Attendance blocks */}
        {(data.attendance || []).filter(a => a.check_in && a.check_out).map((a, i) => {
          const s = parseTime(a.check_in);
          const e = parseTime(a.check_out);
          return (
            <div key={`att-${i}`} className="absolute top-0 h-full bg-gray-200 dark:bg-gray-700/30 rounded"
              style={{ left: `${pct(s)}%`, width: `${widthPct(s, e)}%` }}
              title={`${t('idleTime.attendance')}: ${a.check_in} – ${a.check_out}`} />
          );
        })}

        {/* Pause blocks */}
        {(data.pauses || []).map((p, i) => {
          const s = parseTime(p.start);
          const e = parseTime(p.end);
          return (
            <div key={`pause-${i}`} className="absolute top-0 h-full rounded"
              style={{ left: `${pct(s)}%`, width: `${widthPct(s, e)}%`, backgroundColor: GRAY, opacity: 0.6 }}
              title={`${t('idleTime.pauseTime')}: ${p.start} – ${p.end} (${p.duration_minutes} min)`} />
          );
        })}

        {/* Activity blocks (Violet) */}
        {activities.map((a, i) => {
          const s = parseTime(a.start);
          const e = parseTime(a.end);
          return (
            <div key={`act-${i}`} className="absolute top-1 h-10 rounded-sm opacity-80"
              style={{ left: `${pct(s)}%`, width: `${Math.max(widthPct(s, e), 0.3)}%`, backgroundColor: VIOLET }}
              title={`${a.type}: ${a.start} – ${a.end} (${a.duration_minutes} min)`} />
          );
        })}

        {/* Gap indicators (Cyan for normal, Amber for outliers) */}
        {(data.gaps || []).filter(g => g.idle_minutes > 0.5).map((g, i) => {
          const s = parseTime(g.start);
          const e = parseTime(g.end);
          const color = g.is_outlier ? AMBER : CYAN;
          return (
            <div key={`gap-${i}`} className="absolute top-0 h-full border-l border-r rounded-sm"
              style={{
                left: `${pct(s)}%`,
                width: `${Math.max(widthPct(s, e), 0.2)}%`,
                backgroundColor: `${color}20`,
                borderColor: `${color}60`,
              }}
              title={`Idle: ${g.start} – ${g.end} (${g.idle_minutes} min)${g.is_outlier ? ' [Outlier]' : ''}`} />
          );
        })}
      </div>

      {/* Hour markers */}
      <div className="relative h-4 mt-1">
        {hours.map(h => (
          <span key={h} className="absolute text-[9px] text-[var(--text-secondary)] -translate-x-1/2"
            style={{ left: `${pct(h * 60)}%` }}>
            {String(h).padStart(2, '0')}
          </span>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 text-[10px] text-[var(--text-secondary)]">
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm inline-block" style={{ backgroundColor: VIOLET }} /> {t('idleTime.activities')}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm inline-block" style={{ backgroundColor: CYAN, opacity: 0.6 }} /> {t('idleTime.idleTime')}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm inline-block" style={{ backgroundColor: AMBER, opacity: 0.6 }} /> {t('idleTime.outlier')}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm inline-block" style={{ backgroundColor: GRAY }} /> {t('idleTime.pauseTime')}</span>
      </div>
    </div>
  );
}
